import React, { useMemo, useState } from 'react';
import { Alert, Button, Empty, Input, Select, Space, Spin, Table, Tag, Typography } from 'antd';
import PageTabs from '../page/PageTabs';
import { PlusOutlined, RobotOutlined } from '@ant-design/icons';
import AiDraftBadge from '../AiDraftBadge';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import LibraryItemFormModal from './LibraryItemFormModal';
import LibraryAiDraftModal from './LibraryAiDraftModal';
import LibraryItemDrawer from './LibraryItemDrawer';
import LibraryCoveragePanel from './LibraryCoveragePanel';
import { formatDdMmYyyy } from '../../utils/trainingModel';
import {
    LIBRARY_FAMILIES,
    LIBRARY_STATUSES,
    LIBRARY_STATUS_LABELS,
    itemNeedsHumanReview,
    standardOptionsFrom,
} from '../../utils/libraryTemplateModel';

const { Text } = Typography;
const STATUS_COLOURS = { draft: 'default', in_review: 'gold', approved: 'blue', published: 'green', retired: 'red' };

/**
 * Policy Hub template library curator — platform_admin only (every /admin/library-templates
 * route is requireRole('platform_admin'); App-level nav shows this to that persona alone).
 * The list is filtered server-side (standard / status / family / control / title search) —
 * nothing is filtered in the client.
 *
 * ISO/IEC 27001:2022 Cl. 7.5.3 (documented information, version control) and A.5.37
 * (documented operating procedures); .claude/design/library-curator.md.
 */
const LibraryCuratorConsole = () => {
    const { request } = useApiRequest();
    const [filters, setFilters] = useState({ standardId: null, status: null, family: null, controlRef: '', q: '' });
    const [draftInputs, setDraftInputs] = useState({ controlRef: '', q: '' });
    const [formOpen, setFormOpen] = useState(false);
    const [aiOpen, setAiOpen] = useState({ open: false, standardId: null, controlRef: '' });
    const [openItemId, setOpenItemId] = useState(null);

    const standards = useResource(
        async () => (await request('GET', '/standards')).data.standards || [],
        [request],
    );
    const standardOptions = useMemo(() => standardOptionsFrom(standards.data), [standards.data]);
    const standardLabel = (id) => standardOptions.find((o) => o.value === id)?.label || id;

    const items = useResource(
        async () =>
            (await request('GET', '/admin/library-templates', {
                query: {
                    standardId: filters.standardId,
                    status: filters.status,
                    family: filters.family,
                    // controlRef is only sent with a standard — the API canonicalises it per standard.
                    controlRef: filters.standardId ? filters.controlRef : undefined,
                    q: filters.q,
                },
            })).data.items || [],
        [filters, request],
    );

    const applyTextFilters = () => setFilters((f) => ({ ...f, controlRef: draftInputs.controlRef.trim(), q: draftInputs.q.trim() }));

    const columns = [
        {
            title: 'Control',
            key: 'control',
            width: 230,
            render: (_, r) => (
                <div>
                    <Text strong>{r.controlRef}</Text>
                    <div><Text type="secondary" style={{ fontSize: 12 }}>{standardLabel(r.standardId)}</Text></div>
                </div>
            ),
        },
        { title: 'Title', dataIndex: 'title', key: 'title' },
        { title: 'Version', key: 'version', width: 120, render: (_, r) => <Space>v{r.version}{r.isCurrent && <Tag color="green">Current</Tag>}</Space> },
        { title: 'Status', dataIndex: 'status', key: 'status', width: 120, render: (s) => <Tag color={STATUS_COLOURS[s]}>{LIBRARY_STATUS_LABELS[s] || s}</Tag> },
        {
            title: 'Source',
            key: 'source',
            width: 300,
            render: (_, r) =>
                r.sourceType === 'ai_generated' ? (
                    <AiDraftBadge sourceType={r.sourceType} confidence={r.confidence} reviewed={!itemNeedsHumanReview(r)} reviewedAt={r.reviewedAt} draftLabel="AI draft - needs approval" />
                ) : (
                    <Text type="secondary">Human</Text>
                ),
        },
        { title: 'Created', dataIndex: 'createdAt', key: 'createdAt', width: 110, render: (d) => formatDdMmYyyy(d) },
        { title: '', key: 'open', width: 90, render: (_, r) => <Button size="small" onClick={() => setOpenItemId(r.id)}>Open</Button> },
    ];

    const list = (
        <div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
                <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    aria-label="Filter by standard"
                    placeholder="Standard"
                    style={{ minWidth: 220 }}
                    value={filters.standardId}
                    onChange={(v) => setFilters((f) => ({ ...f, standardId: v || null }))}
                    options={standardOptions}
                />
                <Select
                    allowClear
                    aria-label="Filter by status"
                    placeholder="Status"
                    style={{ minWidth: 140 }}
                    value={filters.status}
                    onChange={(v) => setFilters((f) => ({ ...f, status: v || null }))}
                    options={LIBRARY_STATUSES.map((s) => ({ value: s, label: LIBRARY_STATUS_LABELS[s] }))}
                />
                <Select
                    allowClear
                    aria-label="Filter by family"
                    placeholder="Family"
                    style={{ minWidth: 150 }}
                    value={filters.family}
                    onChange={(v) => setFilters((f) => ({ ...f, family: v || null }))}
                    options={LIBRARY_FAMILIES.map((f) => ({ value: f, label: f }))}
                />
                <Input
                    aria-label="Filter by control reference"
                    placeholder="Control ref (needs a standard)"
                    style={{ width: 210 }}
                    disabled={!filters.standardId}
                    value={draftInputs.controlRef}
                    onChange={(e) => setDraftInputs((d) => ({ ...d, controlRef: e.target.value }))}
                    onPressEnter={applyTextFilters}
                />
                <Input.Search
                    aria-label="Search titles"
                    placeholder="Search titles"
                    style={{ width: 220 }}
                    value={draftInputs.q}
                    onChange={(e) => setDraftInputs((d) => ({ ...d, q: e.target.value }))}
                    onSearch={applyTextFilters}
                />
            </div>

            {items.status === 'loading' && !items.data && <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>}
            {items.status === 'error' && (
                <Alert type="error" showIcon message="Could not load library templates" description={items.error?.message} action={<Button size="small" onClick={items.reload}>Retry</Button>} />
            )}
            {items.status === 'ready' && (items.data || []).length === 0 && (
                <Empty description="No library templates match. Draft one with AI from the Policy Hub, or create one by hand." />
            )}
            {(items.data || []).length > 0 && items.status !== 'error' && (
                <Table rowKey={(r) => r.id} columns={columns} dataSource={items.data} pagination={{ pageSize: 25, hideOnSinglePage: true }} loading={items.status === 'loading'} />
            )}
        </div>
    );

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                <Text type="secondary">
                    The curated, versioned template library projects draw Stage-7 control documents from. Nothing is live until a human approves and publishes it.
                </Text>
                <Space>
                    <Button icon={<RobotOutlined />} onClick={() => setAiOpen({ open: true, standardId: null, controlRef: '' })}>Draft with AI</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setFormOpen(true)}>New template</Button>
                </Space>
            </div>
            {standards.status === 'error' && (
                <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="Could not load the standards list" description="Standard pickers are unavailable until it loads." action={<Button size="small" onClick={standards.reload}>Retry</Button>} />
            )}
            <PageTabs
                defaultActiveKey="templates"
                items={[
                    { key: 'templates', label: 'Templates', children: list },
                    {
                        key: 'coverage',
                        label: 'Coverage',
                        children: (
                            <LibraryCoveragePanel
                                standardOptions={standardOptions}
                                onOpenItem={setOpenItemId}
                                onDraftWithAi={(standardId, controlRef) => setAiOpen({ open: true, standardId, controlRef })}
                            />
                        ),
                    },
                ]}
            />

            <LibraryItemFormModal
                open={formOpen}
                item={null}
                standardOptions={standardOptions}
                onClose={() => setFormOpen(false)}
                onSaved={(item) => {
                    setFormOpen(false);
                    items.reload();
                    setOpenItemId(item.id);
                }}
            />
            <LibraryAiDraftModal
                open={aiOpen.open}
                standardOptions={standardOptions}
                presetStandardId={aiOpen.standardId}
                presetControlRef={aiOpen.controlRef}
                onClose={() => setAiOpen({ open: false, standardId: null, controlRef: '' })}
                onDrafted={(item) => {
                    setAiOpen({ open: false, standardId: null, controlRef: '' });
                    items.reload();
                    setOpenItemId(item.id);
                }}
            />
            <LibraryItemDrawer
                open={!!openItemId}
                itemId={openItemId}
                standardLabel={standardLabel}
                onClose={() => setOpenItemId(null)}
                onChanged={items.reload}
                onOpenItem={setOpenItemId}
            />
        </div>
    );
};

export default LibraryCuratorConsole;
